/**
 * Plan 123 §11 — times shown to an operator are Eastern, wherever the
 * operator and their browser happen to be.
 *
 * Every timestamp the request endpoints carry is UTC. An operator reading a
 * send history has to line it up with a phone call and a homeowner's day, and
 * the fleet's day is Eastern, so the tab converts once here and names the zone
 * once on screen rather than printing an offset on every line.
 *
 * `Intl` does the conversion, so the summer/winter offset is the zone's, not
 * an assumption: 14:30 UTC is 09:30 in January and 10:30 in July.
 */

const EASTERN = 'America/New_York';

/** The one zone label the tab prints, so no line has to carry its own. */
export const EASTERN_ZONE_LABEL = 'Eastern time';

const toDate = (value: string | number | Date | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const partsIn = (date: Date, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-US', { timeZone: EASTERN, ...options }).formatToParts(date);

const pick = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string =>
  parts.find((part) => part.type === type)?.value ?? '';

/** A UTC instant as the Eastern calendar day, `YYYY-MM-DD`. */
export function easternDate(
  value: string | number | Date | null | undefined,
  fallback = '—',
): string {
  const date = toDate(value);
  if (!date) return fallback;
  const parts = partsIn(date, { year: 'numeric', month: '2-digit', day: '2-digit' });
  return `${pick(parts, 'year')}-${pick(parts, 'month')}-${pick(parts, 'day')}`;
}

/** A UTC instant as an Eastern day and clock time, `YYYY-MM-DD HH:MM`. */
export function easternDateTime(
  value: string | number | Date | null | undefined,
  fallback = '—',
): string {
  const date = toDate(value);
  if (!date) return fallback;
  const parts = partsIn(date, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  return (
    `${pick(parts, 'year')}-${pick(parts, 'month')}-${pick(parts, 'day')}`
    + ` ${pick(parts, 'hour')}:${pick(parts, 'minute')}`
  );
}
